<template>
  <div>
    <v-card class="pa-3 assessment-light" flat>
      <v-tabs v-model="tab" fixed-tabs hide-slider :height="(activeTabs.length > 1) ? '2em' : 0">
        <v-tab :id="'slider-tab-' + t.unit" active-class="active-tab" v-for="t, i in activeTabs" :key="i"
          @change="newTab">
          <mrx-translation :token="tabToken(t.unit)"></mrx-translation>
        </v-tab>
        <v-tabs-items class="assessment-transparent" v-for="t, i in activeTabs" :key="i">
          <v-container class="px-5">
            <v-row justify="center" align="center">
              <h1 id="value-display" class="black--text text-center mt-3" style="font-size: 20px; font-weight: 600;">
                <mrx-translation v-if="items[0].unknown" token="app.inputs.unknown.display"></mrx-translation>
                <mrx-translation v-else :prepend="displayValue" :token="displayUnit"></mrx-translation>
              </h1>
            </v-row>

            <v-row justify="space-between" align="center">
              <v-btn fab small color="var(--primary-blue-30)" depressed id="decrement-button" @click="decrement">
                <v-icon size=36 color=primaryblue>{{ icons.mdiMinus }}</v-icon>
              </v-btn>

              <v-slider id="slider" class="mt-5 mx-5" v-model="input" :max="current.max"
                :min="current.min - current.step" :step="current.step" @change="setValue" thumb-color=primaryblue
                color="var(--background-blue)" track-color="var(--primary-blue-30)"></v-slider>

              <v-btn fab small color="var(--primary-blue-30)" depressed id="increment-button" @click="increment">
                <v-icon size=36 color=primaryblue>{{ icons.mdiPlus }}</v-icon>
              </v-btn>
            </v-row>

            <v-row>
              <v-col cols="6" class="pa-0 pe-2">
                <mrx-translation class="black--text" :prepend="displayMin" :token="displayUnit"></mrx-translation>
              </v-col>
              <v-col cols="6" class="text-end pa-0 ps-2">
                <mrx-translation class="black--text" :prepend="displayMax.toString()"
                  :token="displayUnit"></mrx-translation>
              </v-col>
            </v-row>
          </v-container>

          <v-card-actions class="justify-center" v-if="options.allow_unknown">
            <unknown-button id="slider-unknown" @click="setUnknown"></unknown-button>
          </v-card-actions>
        </v-tabs-items>
      </v-tabs>
    </v-card>
  </div>
</template>

<script>
import { convert } from '@/utils/conversions'
import displayFormats from '@/mixins/displayFormats'
import languageLogic from '@/mixins/languageLogic'
import icons from '@/mixins/icons'
import rateRanges from '@/mixins/rateRanges'
import UnknownButton from '../widgets/UnknownButton.vue'
import { mapActions } from 'vuex'

export default {
  name: 'slider',
  components: { UnknownButton },
  mixins: [displayFormats, languageLogic, icons, rateRanges],
  model: {
    prop: 'items',
    event: 'click'
  },
  data() {
    return {
      tab: 0,
      input: null,
      standard: {
        min: 0,
        max: 100,
        unit: null,
        decimalPlaces: 0,
      }
    }
  },
  props: {
    'options': Object,
    'items': Array,
    'panelName': String
  },
  methods: {
    newTab() {
      this.$nextTick(() => {
        let to = this.tab
        let from = this.tab === 0 ? 1 : 0
        this.input = convert(this.activeTabs[from].unit, this.activeTabs[to].unit, this.input, this.activeTabs[to].decimalPlaces)
      })
    },
    tabToken(unit) {
      return `units.${unit}.title`
    },
    increment() {
      if (this.input + this.current.step <= this.current.max) this.input += this.current.step
      this.setValue()
      this.$track(this.$analyticsEvents.SLIDER_INCREMENT, {
        component: 'Slider',
        panel_name: this.panelName,
        value: this.input,
        unit: this.current.unit
      })
    },
    decrement() {
      if (this.input >= this.current.min) this.input -= this.current.step
      this.$nextTick(() => { this.setValue() })
      this.$track(this.$analyticsEvents.SLIDER_DECREMENT, {
        component: 'Slider',
        panel_name: this.panelName,
        value: this.input,
        unit: this.current.unit
      })
    },
    async setUnknown() {
      this.input = this.current.min - this.current.step
      this.items[0].value = null
      this.items[0].unknown = true
      this.items[0].selected = true
      this.clearRangeValidation()
      this.$track(this.$analyticsEvents.SLIDER_UNKNOWN, {
        component: 'Slider',
        panel_name: this.panelName
      })
      if (this.autoProgress) {
        this.next()
      }
    },
    getCanonicalValue() {
      if (this.options.saveUnit) {
        return convert(this.current.unit, this.options.saveUnit, this.input, this.current.decimalPlaces)
      }
      if (this.tab === 0) return this.input.toFixed(this.current.decimalPlaces)
      return convert(
        this.current.unit,
        this.activeTabs[0].unit,
        this.input,
        this.activeTabs[0].decimalPlaces
      )
    },
    setValue() {
      this.items[0].unknown = false
      if (this.input < this.current.min) {
        this.items[0].selected = false
        this.items[0].value = null
        this.clearRangeValidation()
      }
      else {
        const convertedValue = this.getCanonicalValue()
        const validation = this.validateObservationRange(convertedValue)
        const accepted = validation.severity !== 'blocking'
        this.items[0].value = accepted ? convertedValue : null
        this.items[0].selected = accepted
        this.$track(this.$analyticsEvents.SLIDER_CHANGE, {
          component: 'Slider',
          panel_name: this.panelName,
          value: convertedValue,
          unit: this.current.unit,
          display_value: this.input
        })
      }
    },
    setInitial() {
      this.tab = 0

      const hasCurrentValue = !!this.items[0].value
      const isUnknown = this.items[0].unknown

      if (hasCurrentValue && !isUnknown) {
        this.options.saveUnit
          ? this.input = convert(this.options.saveUnit, this.current.unit, this.items[0].value, this.current.decimalPlaces)
          : this.input = this.items[0].value
      } else {
        this.input = this.current.min - this.current.step
      }
    },
    ...mapActions({ next: 'v4/navigate/next' })
  },
  computed: {
    autoProgress() {
      return this.$store.state.settings.engagements_features.autoProgress
    },
    activeTabs() {
      let base = Object.assign({}, this.standard, this.options)
      base.step = this.options.step || 1 / (10 ** base.decimalPlaces)
      let results = [base]
      if (this.options.altUnit) {
        results.push({
          min: convert(base.unit, this.options.altUnit, base.min, base.decimalPlaces),
          max: convert(base.unit, this.options.altUnit, base.max, base.decimalPlaces),
          unit: this.options.altUnit,
          step: base.step,
          decimalPlaces: base.decimalPlaces,
        })
      }
      return results
    },
    current() {
      return this.activeTabs[this.tab]
    },
    displayUnit() {
      return this.current.unit ? `units.${this.current.unit}.short` : ''
    },
    displayMin() {
      return this.$i18n.n(this.current.min, 'decimal').toString()
    },
    displayMax() {
      return this.$i18n.n(this.current.max, 'decimal').toString()
    },
    displayValue() {
      return this.input < this.current.min ? null : this.$i18n.n(this.input, 'decimal').toString()
    },
  },
  created() {
    this.setInitial()
  },
  watch: {
    panelName: function () {
      this.setInitial()
    }
  }
}
</script>

<style>
.active-tab {
  border-radius: 4px;
  background-color: white;
  color: deepblue;
  font-size: 16px;
  font-weight: 600;
  border-style: solid;
  border-color: var(--primary-blue-10);
  border-width: 2px;
}

.v-tab {
  text-transform: none;
}
</style>
